/**
 * Field values for GraphDetails (W8 D5): how a deal fact or custom field is
 * shown, edited and sent back. Values travel as the API has them; a form input
 * holds a string, and an emptied input means null (clear the field).
 */

export type DetailValue = string | number | boolean | null;

export type DetailType = "text" | "number" | "date" | "boolean" | "choice";

export interface DetailField {
  key: string;
  label: string;
  type: DetailType;
  choices?: { value: string; label: string }[];
  value: DetailValue;
  /** Read-mode text for a set value, when the raw value is not it ("6.625%"). */
  format?: (value: string | number | boolean) => string;
}

/** A custom field as the API lists it on a deal or contact. */
export interface CustomField {
  key: string;
  label: string;
  data_type: DetailType;
  choices: string[] | null;
  value: DetailValue;
}

/** Custom-field detail keys carry this prefix, so they never collide with a fact. */
export const CUSTOM_PREFIX = "custom_fields.";

const DATE = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeZone: "UTC",
});

export function toInput(value: DetailValue): string {
  return value == null ? "" : String(value);
}

export function fromInput(field: DetailField, input: string): DetailValue {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  if (field.type === "number") return Number(trimmed);
  if (field.type === "boolean") return trimmed === "true";
  return trimmed;
}

/** `{ key: value }` for each field whose input no longer matches its value. */
export function changedValues(
  fields: DetailField[],
  draft: Record<string, string>,
): Record<string, DetailValue> {
  const changes: Record<string, DetailValue> = {};
  for (const field of fields) {
    const next = fromInput(field, draft[field.key] ?? "");
    if (next !== field.value) changes[field.key] = next;
  }
  return changes;
}

export function displayValue(field: DetailField): string {
  const { value } = field;
  if (value == null || value === "") return "—";
  if (field.format) return field.format(value);
  if (field.type === "boolean") return value ? "Yes" : "No";
  if (field.type === "date") {
    return DATE.format(new Date(`${String(value)}T00:00:00Z`));
  }
  const choice = field.choices?.find((c) => c.value === value);
  return choice ? choice.label : String(value);
}

export function customDetailFields(fields: CustomField[]): DetailField[] {
  return fields.map((field) => ({
    key: `${CUSTOM_PREFIX}${field.key}`,
    label: field.label,
    type: field.data_type,
    choices: field.choices?.map((choice) => ({ value: choice, label: choice })),
    value: field.value,
  }));
}

/**
 * The PATCH body for `changes`: top-level keys as they are, custom fields
 * gathered under `custom_fields`.
 */
export function detailsPatch(
  changes: Record<string, DetailValue>,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const custom: Record<string, DetailValue> = {};
  for (const [key, value] of Object.entries(changes)) {
    if (key.startsWith(CUSTOM_PREFIX)) {
      custom[key.slice(CUSTOM_PREFIX.length)] = value;
    } else {
      body[key] = value;
    }
  }
  if (Object.keys(custom).length) body.custom_fields = custom;
  return body;
}
